import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGetData,
  type GetImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGetError,
  type GetImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGetResponse,
  getImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGet,
} from '../../generated';

export type GetImageCountQueryVariables = GetImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGetData['path'];

export type GetImageCountQuery = GetImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGetResponse;

export const useGetImageCountQuery = <
  TData = GetImageCountQuery,
  TError = GetImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGetError,
>(
  { portfolio_id, pdp_id }: GetImageCountQueryVariables,
  options?: Omit<UseQueryOptions<GetImageCountQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageCountQuery, TError, TData>({
    queryKey: ['GetImageCount', { portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageCountQuery;
    },
    ...options,
  });
};

useGetImageCountQuery.getKey = ({ portfolio_id, pdp_id }: GetImageCountQueryVariables) => [
  'GetImageCount',
  { portfolio_id, pdp_id },
];

useGetImageCountQuery.fetcher = (
  { portfolio_id, pdp_id }: GetImageCountQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageCountV1PortfoliosPortfolioIdPdpsPdpIdImageCountGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageCountQuery;
  };
};

useGetImageCountQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/image-count',
};
